import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { changelogData, type ChangelogEntry } from '../data/changelog-data';
import { releaseTests, type ReleaseTest } from '../data/release-tests-data';
import type { TestResultData } from './useTestResults';

// --- Types ---

type ChangeType = 'feature' | 'fix' | 'improvement' | 'breaking';
type TestStatus = 'passed' | 'failed' | 'not_tested';

export interface ReleaseFilters {
  query: string;
  versions: string[];
  categories: string[];
  types: ChangeType[];
  statuses: TestStatus[];
  showAllVersions: boolean;
}

export interface UseReleaseFiltersReturn {
  filters: ReleaseFilters;
  setQuery: (q: string) => void;
  toggleVersion: (v: string) => void;
  toggleCategory: (c: string) => void;
  toggleType: (t: ChangeType) => void;
  toggleStatus: (s: TestStatus) => void;
  setShowAllVersions: (show: boolean) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  filteredChangelog: ChangelogEntry[];
  filteredTests: ReleaseTest[];
  filteredTestsWithStatus: (results: TestResultData[]) => ReleaseTest[];
  allVersions: string[];
  visibleVersions: string[];
  allCategories: string[];
  changelogCount: { filtered: number; total: number };
  testCount: { filtered: number; total: number };
  hiddenVersionCount: number;
}

const DEFAULT_VISIBLE_VERSIONS = 3;

// --- Helpers ---

function parseArrayParam(params: URLSearchParams, key: string): string[] {
  const val = params.get(key);
  return val ? val.split(',').filter(Boolean) : [];
}

function matchesQuery(text: string | undefined, query: string): boolean {
  return !!text && text.toLowerCase().includes(query);
}

// --- Hook ---

export function useReleaseFilters(): UseReleaseFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial state from URL
  const [filters, setFilters] = useState<ReleaseFilters>(() => ({
    query: searchParams.get('q') ?? '',
    versions: parseArrayParam(searchParams, 'v'),
    categories: parseArrayParam(searchParams, 'cat'),
    types: parseArrayParam(searchParams, 'type') as ChangeType[],
    statuses: parseArrayParam(searchParams, 'status') as TestStatus[],
    showAllVersions: searchParams.get('all') === '1',
  }));

  // Debounce query for URL sync
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState(filters.query);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(filters.query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.query]);

  // Sync filters → URL (preserve tab param)
  useEffect(() => {
    const params: Record<string, string> = {};
    const tab = searchParams.get('tab');
    if (tab) params['tab'] = tab;
    if (debouncedQuery) params['q'] = debouncedQuery;
    if (filters.versions.length) params['v'] = filters.versions.join(',');
    if (filters.categories.length) params['cat'] = filters.categories.join(',');
    if (filters.types.length) params['type'] = filters.types.join(',');
    if (filters.statuses.length) params['status'] = filters.statuses.join(',');
    if (filters.showAllVersions) params['all'] = '1';
    setSearchParams(params, { replace: true });
  }, [
    debouncedQuery,
    filters.versions,
    filters.categories,
    filters.types,
    filters.statuses,
    filters.showAllVersions,
    searchParams,
    setSearchParams,
  ]);

  // --- All versions & categories (derived from data) ---

  const allVersions = useMemo(() => {
    const vSet = new Set<string>();
    changelogData.forEach(e => vSet.add(e.version));
    releaseTests.forEach(e => vSet.add(e.version));
    return Array.from(vSet);
  }, []);

  const visibleVersions = useMemo(() => {
    if (filters.showAllVersions) return allVersions;
    return allVersions.slice(0, DEFAULT_VISIBLE_VERSIONS);
  }, [allVersions, filters.showAllVersions]);

  const hiddenVersionCount = Math.max(0, allVersions.length - DEFAULT_VISIBLE_VERSIONS);

  const allCategories = useMemo(() => {
    const cSet = new Set<string>();
    changelogData.forEach(e => e.changes.forEach(c => cSet.add(c.category)));
    return Array.from(cSet).sort();
  }, []);

  // --- Filter logic ---

  const versionFilter = useCallback(
    (version: string) => {
      if (filters.versions.length === 0) return true;
      return filters.versions.includes(version);
    },
    [filters.versions]
  );

  const filteredChangelog = useMemo(() => {
    const q = filters.query.toLowerCase();
    const entries = (
      filters.showAllVersions
        ? changelogData
        : changelogData.filter(e => visibleVersions.includes(e.version))
    ).filter(entry => versionFilter(entry.version));

    if (!q && filters.categories.length === 0 && filters.types.length === 0) {
      return entries;
    }

    return entries
      .map(entry => {
        const filteredChanges = entry.changes.filter(c => {
          if (filters.categories.length > 0 && !filters.categories.includes(c.category))
            return false;
          if (filters.types.length > 0 && !filters.types.includes(c.type)) return false;
          if (q) {
            return (
              matchesQuery(c.titleEn, q) ||
              matchesQuery(c.titleHu, q) ||
              matchesQuery(c.titleDe, q) ||
              matchesQuery(c.titleUk, q) ||
              matchesQuery(c.descriptionEn, q) ||
              matchesQuery(c.descriptionHu, q) ||
              matchesQuery(c.descriptionDe, q) ||
              matchesQuery(c.descriptionUk, q) ||
              matchesQuery(c.category, q)
            );
          }
          return true;
        });
        if (filteredChanges.length === 0) return null;
        return { ...entry, changes: filteredChanges };
      })
      .filter((e): e is ChangelogEntry => e !== null);
  }, [
    filters.query,
    filters.categories,
    filters.types,
    filters.showAllVersions,
    visibleVersions,
    versionFilter,
  ]);

  const filteredTests = useMemo(() => {
    const q = filters.query.toLowerCase();
    const entries = (
      filters.showAllVersions
        ? releaseTests
        : releaseTests.filter(e => visibleVersions.includes(e.version))
    ).filter(entry => versionFilter(entry.version));

    if (!q) return entries;

    return entries
      .map(entry => {
        const filteredFeatures = entry.features.filter(
          f =>
            matchesQuery(f.nameEn, q) ||
            matchesQuery(f.nameHu, q) ||
            matchesQuery(f.nameDe, q) ||
            matchesQuery(f.nameUk, q)
        );
        if (filteredFeatures.length === 0) return null;
        return { ...entry, features: filteredFeatures };
      })
      .filter((e): e is ReleaseTest => e !== null);
  }, [filters.query, filters.showAllVersions, visibleVersions, versionFilter]);

  // Status filter for testing tab (needs test results from API)
  const filteredTestsWithStatus = useCallback(
    (results: TestResultData[]): ReleaseTest[] => {
      if (filters.statuses.length === 0) return filteredTests;

      return filteredTests
        .map(entry => {
          const filtered = entry.features.filter((_, fIdx) => {
            const result = results.find(
              r => r.version === entry.version && r.featureIndex === fIdx
            );
            const status: TestStatus = result?.status ?? 'not_tested';
            return filters.statuses.includes(status);
          });
          if (filtered.length === 0) return null;
          return { ...entry, features: filtered };
        })
        .filter((e): e is ReleaseTest => e !== null);
    },
    [filteredTests, filters.statuses]
  );

  // --- Counts ---

  const totalChangelogChanges = changelogData.reduce((a, e) => a + e.changes.length, 0);
  const filteredChangelogChanges = filteredChangelog.reduce((a, e) => a + e.changes.length, 0);

  const totalTestFeatures = releaseTests.reduce((a, e) => a + e.features.length, 0);
  const filteredTestFeatures = filteredTests.reduce((a, e) => a + e.features.length, 0);

  // --- Mutators ---

  const toggle = <T>(arr: T[], item: T): T[] =>
    arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];

  const setQuery = useCallback((q: string) => setFilters(f => ({ ...f, query: q })), []);
  const toggleVersion = useCallback(
    (v: string) => setFilters(f => ({ ...f, versions: toggle(f.versions, v) })),
    []
  );
  const toggleCategory = useCallback(
    (c: string) => setFilters(f => ({ ...f, categories: toggle(f.categories, c) })),
    []
  );
  const toggleType = useCallback(
    (t: ChangeType) => setFilters(f => ({ ...f, types: toggle(f.types, t) })),
    []
  );
  const toggleStatus = useCallback(
    (s: TestStatus) => setFilters(f => ({ ...f, statuses: toggle(f.statuses, s) })),
    []
  );
  const setShowAllVersions = useCallback(
    (show: boolean) => setFilters(f => ({ ...f, showAllVersions: show })),
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(f => ({
      query: '',
      versions: [],
      categories: [],
      types: [],
      statuses: [],
      showAllVersions: f.showAllVersions,
    }));
  }, []);

  const hasActiveFilters =
    filters.query !== '' ||
    filters.versions.length > 0 ||
    filters.categories.length > 0 ||
    filters.types.length > 0 ||
    filters.statuses.length > 0;

  return {
    filters,
    setQuery,
    toggleVersion,
    toggleCategory,
    toggleType,
    toggleStatus,
    setShowAllVersions,
    clearFilters,
    hasActiveFilters,
    filteredChangelog,
    filteredTests,
    filteredTestsWithStatus,
    allVersions,
    visibleVersions,
    allCategories,
    changelogCount: { filtered: filteredChangelogChanges, total: totalChangelogChanges },
    testCount: { filtered: filteredTestFeatures, total: totalTestFeatures },
    hiddenVersionCount,
  };
}
